import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, logoUrl, faviconUrl } from "@/lib/api";

const STATIC_MODE = process.env.REACT_APP_STATIC_CONTENT === 'true';

const LINKS = [
  { href: "#servicios",    label: "Servicios" },
  { href: "#broadcasting", label: "Broadcasting" },
  { href: "#nosotros",     label: "Nosotros" },
  { href: "#galeria",      label: "Trabajos" },
  { href: "#clientes",     label: "Clientes" },
  { href: "#contacto",     label: "Contacto" },
];

// ── Timing ────────────────────────────────────────────────────
const T_LOGO    = 7500;   // logo visible + papelitos
const T_OUT     =  420;   // logo fade-out
const T_MARQUEE = 16200;  // 2 × 8s marquee
const T_MQ_OUT  =  420;   // marquee fade-out

// ── Confetti ──────────────────────────────────────────────────
const COLS = ['#75AADB', '#75AADB', '#ffffff', '#ffffff', '#aad4f5'];

function makePiece(cx, spreadW, canvasH) {
  return {
    x:   cx + (Math.random() - 0.5) * spreadW,
    y:   -6 - Math.random() * canvasH * 0.3,
    vx:  (Math.random() - 0.5) * 0.9,
    vy:  0.35 + Math.random() * 0.55,
    pw:  4 + Math.random() * 4,
    ph:  2 + Math.random() * 2,
    ang: Math.random() * Math.PI * 2,
    spin:(Math.random() - 0.5) * 0.12,
    col: COLS[Math.floor(Math.random() * COLS.length)],
    circ:Math.random() < 0.25,
    op:  0.8 + Math.random() * 0.2,
  };
}

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [phase, setPhase]       = useState('logo'); // logo | out | marquee | mq-out
  const [mqKey, setMqKey]       = useState(0);

  const headerRef = useRef(null);
  const cvRef     = useRef(null);
  const brandRef  = useRef(null);
  const pieces    = useRef([]);
  const frameRef  = useRef(null);
  const doSpawn   = useRef(true); // true ONLY during logo phase

  const [brandData, setBrandData]       = useState(null);
  const [brandLoading, setBrandLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        let brand;
        if (STATIC_MODE) {
          const res  = await fetch('/data/content.json');
          const json = await res.json();
          brand = json?.content?.brand;
        } else {
          const res = await api.get("/public/home");
          brand = res.data?.content?.brand;
        }
        setBrandData(brand ?? null);
      } catch (e) {
        console.error("NavBar: error cargando brand", e);
      } finally {
        setBrandLoading(false);
      }
    };
    load();
  }, []);

  const logoFilename    = brandData?.logo_filename;
  const faviconFilename = brandData?.favicon_filename;
  const logoScale       = brandData?.logo_scale    ?? 1.0;
  const logoOffsetX     = brandData?.logo_offset_x ?? 0;
  const logoOffsetY     = brandData?.logo_offset_y ?? 0;

  // Scroll handler
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Favicon dinámico — actualiza <link rel="icon"> cuando el admin sube uno
  useEffect(() => {
    if (!faviconFilename) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl(faviconFilename);
  }, [faviconFilename]);

  // Confetti canvas loop
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');

    const syncSize = () => {
      const h = headerRef.current;
      cv.width  = h ? h.offsetWidth  : window.innerWidth;
      cv.height = h ? h.offsetHeight : 80;
    };
    syncSize();
    window.addEventListener('resize', syncSize);

    function draw() {
      frameRef.current = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, cv.width, cv.height);

      if (doSpawn.current && Math.random() < 0.055) {
        const r  = brandRef.current?.getBoundingClientRect();
        const cx = r ? r.left + r.width * 0.5 : cv.width * 0.07;
        const sw = r ? r.width * 1.2 : 180;
        pieces.current.push(makePiece(cx, sw, cv.height));
      }

      pieces.current = pieces.current.filter(p => {
        p.x  += p.vx;
        p.y  += p.vy;
        p.vy += 0.035;
        p.ang += p.spin;
        if (p.y > cv.height + 8) return false;
        ctx.save();
        ctx.globalAlpha = p.op;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.ang);
        ctx.fillStyle = p.col;
        if (p.circ) {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.pw / 2, p.ph / 2, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.pw / 2, -p.ph / 2, p.pw, p.ph);
        }
        ctx.restore();
        return true;
      });
    }

    frameRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', syncSize);
    };
  }, []); // eslint-disable-line

  // Argentina state machine
  useEffect(() => {
    let tid;
    function cycle() {
      // ── LOGO phase: papelitos ON ──
      setPhase('logo');
      doSpawn.current = true;
      pieces.current  = [];

      tid = setTimeout(() => {
        // ── OUT phase: papelitos OFF, logo fades ──
        setPhase('out');
        doSpawn.current = false;

        tid = setTimeout(() => {
          // ── MARQUEE phase ──
          setPhase('marquee');
          setMqKey(k => k + 1);

          tid = setTimeout(() => {
            // ── MARQUEE-OUT phase ──
            setPhase('mq-out');
            tid = setTimeout(cycle, T_MQ_OUT);
          }, T_MARQUEE);
        }, T_OUT);
      }, T_LOGO);
    }

    cycle();
    return () => clearTimeout(tid);
  }, []); // eslint-disable-line

  const logoHidden = phase !== 'logo';
  const mqInDom    = phase === 'marquee' || phase === 'mq-out';
  const mqVisible  = phase === 'marquee';

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-[2px] left-0 right-0 z-50 overflow-hidden transition-all duration-300 ${
          scrolled ? "py-3 bg-overlay backdrop-blur-md border-b border-line-2" : "py-5 bg-transparent"
        }`}
      >
        {/* Confetti canvas — clipeado por overflow:hidden del header */}
        <canvas
          ref={cvRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 60,
          }}
        />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">

          {/* ── Brand link ── */}
          <a
            ref={brandRef}
            href="#top"
            className="flex items-center gap-3 group"
            style={{ position: 'relative', flexShrink: 0 }}
          >
            {/* Flex-column: slot encima + estrellas debajo */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>

              {/* Slot fijo 150×36px — logo y marquee se superponen aquí */}
              <div style={{ position: 'relative', width: 150, height: 36, overflow: 'hidden', borderRadius: 4 }}>

                {/* ── Logo layer ── */}
                <div style={{
                  position:   'absolute',
                  inset:      0,
                  display:    'flex',
                  alignItems: 'center',
                  opacity:    logoHidden ? 0 : 1,
                  transform:  logoHidden ? 'scale(0.7)' : 'scale(1)',
                  transition: 'opacity 0.42s ease, transform 0.42s ease',
                  pointerEvents: logoHidden ? 'none' : 'auto',
                }}>
                  <BrandMark logoFilename={logoFilename} isLoading={brandLoading} scale={logoScale} offsetX={logoOffsetX} offsetY={logoOffsetY} />
                </div>

                {/* ── Argentina layer ── */}
                {mqInDom && (
                  <div style={{
                    position:      'absolute',
                    inset:         0,
                    display:       'flex',
                    alignItems:    'center',
                    overflow:      'hidden',
                    opacity:       mqVisible ? 1 : 0,
                    transition:    'opacity 0.42s ease',
                    pointerEvents: 'none',
                  }}>
                    {/* Wrapper animado: recibe el translateX */}
                    <span
                      key={mqKey}
                      style={{
                        display:       'inline-block',
                        whiteSpace:    'nowrap',
                        fontSize:      22,
                        fontWeight:    900,
                        fontFamily:    "system-ui, -apple-system, sans-serif",
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        lineHeight:    1,
                        animation:     'arg-marquee 8s linear 2 forwards',
                      }}
                    >
                      {/* Texto tricolor vertical: celeste / blanco / celeste */}
                      <span style={{
                        background:           'linear-gradient(to bottom, #75AADB 33%, #ffffff 33%, #ffffff 66%, #75AADB 66%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor:  'transparent',
                        backgroundClip:       'text',
                      }}>
                        {'  ¡ ¡ ¡ V A M O S     A R G E N T I N A ! ! !  '}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {/* Estrellas doradas — visibles solo durante fase logo */}
              <div style={{
                width:      150,
                textAlign:  'center',
                opacity:    logoHidden ? 0 : 1,
                transition: 'opacity 0.42s ease',
                lineHeight: 1,
                userSelect: 'none',
              }}>
                <span style={{ color: '#F6B40E', fontSize: 18, letterSpacing: '0.25em' }}>★  ★  ★</span>
              </div>

            </div>

            {/* Brand text (only when no logo image AND data already loaded) */}
            {!brandLoading && !logoFilename && (
              <div
                className="leading-tight"
                style={{ opacity: logoHidden ? 0 : 1, transition: 'opacity 0.42s ease', flexShrink: 0 }}
              >
                <div className="text-fg font-semibold text-[17px] tracking-tight">
                  {data?.content?.brand?.name || "LUVA Solutions"}
                </div>
                <div className="text-mute text-[11px] tracking-wide">IT · Broadcasting</div>
              </div>
            )}
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href} className="text-fg-2 hover:text-fg text-[14px] font-medium transition-colors">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="#contacto" className="hidden sm:inline-flex btn-solid items-center gap-1.5 px-4 py-2 text-[13px] rounded-sm">
              Hablar con LUVA <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <button
              className="lg:hidden w-10 h-10 border border-line flex items-center justify-center text-fg"
              onClick={() => setOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[55] bg-black/85 lg:hidden" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            className="absolute right-0 top-0 bottom-0 w-80 bg-surface p-6 border-l border-line"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="text-fg font-semibold">Menú</div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 border border-line flex items-center justify-center text-fg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-1">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block px-3 py-3 border-b border-line-2 text-fg-2 hover:text-fg">
                  {l.label}
                </a>
              ))}
            </div>
            <a href="#contacto" onClick={() => setOpen(false)} className="mt-6 btn-solid w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-sm">
              Hablar con LUVA
            </a>
            <Link to="/admin" className="mt-6 block text-faint text-xs hover:text-fg">/admin →</Link>
          </motion.div>
        </div>
      )}
    </>
  );
}

function BrandMark({ logoFilename, isLoading, scale = 1.0, offsetX = 0, offsetY = 0 }) {
  const [imgError, setImgError] = useState(false);

  if (isLoading) {
    return <div style={{ width: 36, height: 36 }} />;
  }
  if (logoFilename && !imgError) {
    return (
      <img
        src={logoUrl(logoFilename)}
        alt="LUVA Solutions"
        className="h-9 w-auto max-w-[140px]"
        style={{
          objectFit:       'contain',
          transform:       `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
          transformOrigin: 'center center',
        }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="relative w-9 h-9 bg-brand flex items-center justify-center overflow-hidden" style={{ borderRadius: 2 }}>
      <span className="text-white font-bold text-[15px] tracking-tighter">LU</span>
    </div>
  );
}
